import React, { lazy, Suspense, useState, useCallback } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Select,
    Box,
    InputLabel,
    FormControl,
    Typography,
    MenuItem,
    Skeleton,
} from "@mui/material";
import GridForm, { GridFormItem } from "../../../GridForm";
import { Patient as OutreachPatient, Provider } from "../../../../models/core";
import { Place, Patient } from '../../../../models/ziphy'
import RequireZcmIdDialog from "./RequireZcmIdDialog";
import { API } from "../../../../utils/ZiphyAPI";
import { EnumField } from "../../../fields";
import { TIME_ZONES } from "../../../../utils/timezone";
import useDialog from "../../../../hooks/useDialog";
import SimpleDialog from "../../../dialogs/SimpleDialog";
import SpinnerButton from "../../../SpinnerButton";
import AlertSnack from "../../../ErrorSnack";
import useSnackbar from "../../../../hooks/useSnackbar";
import { States } from "../../../../utils/enums";
import { copyAction, ErrorListDialog, useErrorQueue } from "../../../ErrorListDialog";

const PlacesAutocomplete = lazy(() => import('./PlacesAutocomplete'));

export enum AddressDialogMode { ChooseAddress, CreatePlace }

function getInitialAddressString(outreachPatient:OutreachPatient) {
    const addr2 = outreachPatient.address2 && outreachPatient.address2?.trim() !== '' ? ' '+outreachPatient.address2 : '';
    const addressStreet = `${outreachPatient.address||''}${addr2}`;
    const address = `${addressStreet}, ${outreachPatient.city||''}, ${outreachPatient.state||''} ${outreachPatient.zip||''}` || ''
    if(address.trim().length < 6) {
        return undefined;
    }
    return address
}

interface AddressDialogProps {
    open: boolean;
    mode?: AddressDialogMode;
    outreachPatient?: OutreachPatient;
    patient?:Patient;
    practiceId?: number;
    provider?: Provider;
    onClose: () => void;
    onChange?: (place: Place) => void;
}

const AddressDialog: React.FC<AddressDialogProps> = ({
    open,
    mode = AddressDialogMode.ChooseAddress,
    outreachPatient,
    patient,
    practiceId,
    onClose,
    onChange,
}) => {
    const [address, setAddress] = useState<Place>({} as Place);
    const [countryCode, setCountryCode] = useState<string|undefined>();
    const [timeZone, setTimeZone] = useState<string>('America/New_York');
    const [isBusy, setIsBusy] = useState<boolean>(false);
    const verifyDialog = useDialog();
    const { showSuccess:snackSuccess, ...snackProps } = useSnackbar();
    const errorQueue = useErrorQueue();

    const onEnumChange = useCallback((_label: string, value: string | null) => {
        setTimeZone(value || '');
    }, []);

    const requireZCMIdDialog = useCallback((onCloseEvent:() => void) => {
        const isOpen = mode == AddressDialogMode.CreatePlace && (outreachPatient?.zcmId == undefined || outreachPatient?.zcmId == '');
        return isOpen && <RequireZcmIdDialog open={isOpen} onClose={onCloseEvent} />;
    }, [outreachPatient, mode]);

    const createPlace = useCallback(async (address: Place, saveToPatient:boolean) => {
        if (mode != AddressDialogMode.CreatePlace || !outreachPatient || !practiceId) {
            return;
        }
        setIsBusy(true);

        const request = await API.createPlace(address, practiceId).catch((err) => { console.error(err) });
        const place = request as Place;
        if (!place) {
            setIsBusy(false);
            errorQueue.enqueue({message:`Failed to create place: ${address.street}, ${address.city}, ${address.state}, ${address.zip}`})
            return;
        }

        if(saveToPatient && patient && place.id) {
            const req = await API.updatePatient({id:patient.id, place_id:place.id});
            if(!req) {
                setIsBusy(false);
                errorQueue.enqueue({message:`Failed to update patient with address. Please assign from Admin.`})
                return
            }
        }
        try {
            onChange && onChange(place);
            onClose && onClose();
        } catch(error) {
            console.error(error);
            errorQueue.enqueue({message:(error as Error)?.message || 'Failed to update patient places'});
            setIsBusy(false);
            return;
        }
        setIsBusy(false);
        snackSuccess(`Successfully create placed ${address.street}, ${address.city}, ${address.state}, ${address.zip}`);
    }, [outreachPatient, patient, practiceId, snackSuccess, mode, onChange, onClose]);

    const onSelectAddress = useCallback((place: Place, components:Record<string,{long:string, short:string}>) => {
        setAddress(place);
        const country = components['country']?.short || place.country;
        setCountryCode(country)
    }, []);

    return (
        <>
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Enter Address</DialogTitle>
            <DialogContent>
                <Box sx={{ height: '1em', display: 'block' }} />
                <Suspense fallback={<Skeleton variant="rounded" width={'98%'} height={'4em'}/>}> 
                    <PlacesAutocomplete initialPlace={outreachPatient
                        ? getInitialAddressString(outreachPatient)
                        : undefined}
                        open={open} onSelect={onSelectAddress} />
                </Suspense>
                <Box sx={{ height: '3em', display: 'block' }} />
                <GridForm>
                    <GridFormItem xs={4}>
                        <TextField fullWidth label="Building" margin="dense" value={address.building} disabled={!address?.street}
                            onChange={(e) => setAddress({...address, building:e.target.value })} InputLabelProps={{ shrink: !!address.building }} />
                    </GridFormItem>
                    <GridFormItem xs={4}>
                        <TextField fullWidth label="Apartment" margin="dense" value={address.apartment} disabled={!address?.street}
                            onChange={(e) => setAddress({...address, apartment:e.target.value })} InputLabelProps={{ shrink: !!address.apartment }}/>
                    </GridFormItem>
                    <GridFormItem xs={4}>
                        <TextField fullWidth label="Floor" margin="dense" value={address.floor} disabled={!address?.street}
                            onChange={(e) => setAddress({...address, floor:e.target.value })} InputLabelProps={{ shrink: !!address.floor }}/>
                    </GridFormItem>

                    <GridFormItem xs={12}>
                        <TextField fullWidth label="Street" margin="dense" value={address.street} disabled={!address?.street}
                            onChange={(e) => setAddress({...address, street:e.target.value })} InputLabelProps={{ shrink: !!address.street }} />
                    </GridFormItem>
                    
                    <GridFormItem xs={8}>
                        <TextField fullWidth label="City" margin="dense" value={address.city} disabled={!address?.street}
                            onChange={(e) => setAddress({...address, city:e.target.value })} InputLabelProps={{ shrink: !!address.city }}/>
                    </GridFormItem>
                    <GridFormItem xs={2}>
                        <FormControl fullWidth margin="dense" variant='outlined'>
                            <InputLabel id="addr-state">State</InputLabel>
                            <Select id="addr-state" value={address.state || ""} onChange={(e) => setAddress({ ...address, state: e.target.value })}
                                disabled={!address?.street}>
                                {States.map((state) => (<MenuItem key={state.code} value={state.code}>{state.code}</MenuItem>))}
                            </Select>
                        </FormControl>
                    </GridFormItem>
                    <GridFormItem xs={2}>
                        <TextField fullWidth label="Zip" margin="dense" value={address.zip} disabled={true}
                            onChange={(e) => setAddress({...address, zip:e.target.value })} InputLabelProps={{ shrink: !!address.zip }}/>
                    </GridFormItem>

                    <GridFormItem xs={12}>
                        <TextField fullWidth label="Country" margin="dense" value={address.country} disabled={true}
                            onChange={(e) => setAddress({...address, country:e.target.value })} InputLabelProps={{ shrink: !!address.country }}/>
                    </GridFormItem>

                    <GridFormItem xs={6}>
                        <TextField fullWidth label="Latitude" margin="dense" value={address?.coords?.[0]} disabled={true}
                            InputLabelProps={{ shrink: !!address.coords }}/>
                    </GridFormItem>
                    <GridFormItem xs={6}>
                        <TextField fullWidth label="Longitude" margin="dense" value={address?.coords?.[1]} disabled={true}
                            InputLabelProps={{ shrink: !!address.coords }}/>
                    </GridFormItem>
                    <GridFormItem xs={12}>
                        <EnumField
                            name='timezone' label='Time Zone' value={address?.timezone || 'America/New_York'}
                            required onChange={onEnumChange} disabled={!address?.street}
                            options={TIME_ZONES}
                        />
                    </GridFormItem>
                </GridForm>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                <SpinnerButton disabled={!address || !address.street || !address.state} onClick={
                    mode == AddressDialogMode.ChooseAddress
                        ? () => {
                            onChange && onChange({
                                ...address,
                                country:countryCode||'',
                                timezone:timeZone
                            });
                        }
                        : verifyDialog.show
                }
                    color="primary"
                    showSpinner={isBusy}>
                    { mode == AddressDialogMode.CreatePlace ? 'Save' : 'Select' }
                </SpinnerButton>
            </DialogActions>
            {requireZCMIdDialog(onClose)}
            {
                verifyDialog.open ? <SimpleDialog open={verifyDialog.open} title={<b>{'Create Place'}</b>} 
                body={[
                    <Box key='cm-content'>
                        <Typography key='hc1' variant='body1'>{'This will create a new Place in Ziphy Medical.'}</Typography>
                        <br/>
                        <Typography key='hc1' variant='body1'>{'Do you want to continue?'}</Typography>
                    </Box>
                ]}
                buttons={[
                    {text:'Cancel', onClick:verifyDialog.hide},
                    {text:'Create',
                        variant:"contained" as "text" | "contained" | "outlined",
                        onClick:async ()=>{
                        await createPlace({
                            ...address,
                            country:countryCode||'',
                            timezone:timeZone
                        }, true)
                    }}
                ]}
                paperProps={{sx:{minWidth:'400px', minHeight:'20px'}}}
                /> : <></>
            }
            <AlertSnack {...snackProps}/>
        </Dialog>
        <ErrorListDialog
            open={errorQueue.dialogOpen}
            onClose={errorQueue.clear}
            title={errorQueue.title || <b>Attention</b>}
            description={errorQueue.body || <b>The following issues have occured</b>}
            errors={errorQueue.errors}
            globalActions={[copyAction]}
        />
        </>
    );
};

export default AddressDialog;
